import app from 'flarum/forum/app';
import { CalWidgetSkeleton, measure } from './CalSkeleton';
import { userActivity } from '../../common/api';
import { load, peek } from '../../common/widgetData';
import { loc } from '../../common/dates';
const t = (k, p) => app.translator.trans('ernestdefoe-calendar.forum.' + k, p);
const DAY_MS = 86400000;

/** Streak / volume achievement tiers, awarded off longest streak + total posts. */
const STREAK_BADGES = [{
  n: 3,
  icon: '🔥',
  key: 'badge_roll'
}, {
  n: 7,
  icon: '⚡',
  key: 'badge_week'
}, {
  n: 30,
  icon: '🚀',
  key: 'badge_month'
}, {
  n: 100,
  icon: '💯',
  key: 'badge_100'
}, {
  n: 365,
  icon: '👑',
  key: 'badge_year'
}];
const TOTAL_BADGES = [{
  n: 100,
  icon: '✍️',
  key: 'badge_100posts'
}, {
  n: 500,
  icon: '📚',
  key: 'badge_500posts'
}, {
  n: 1000,
  icon: '🏆',
  key: 'badge_1kposts'
}];
function ymd(d) {
  const p = n => String(n).padStart(2, '0');
  return "".concat(d.getFullYear(), "-").concat(p(d.getMonth() + 1), "-").concat(p(d.getDate()));
}
function level(count, max) {
  if (!count) return 0;
  if (max <= 1) return 4;
  const q = count / max;
  return q > 0.75 ? 4 : q > 0.5 ? 3 : q > 0.25 ? 2 : 1;
}

/** Build GitHub-style week columns for the last ~year, aligned to the first weekday. */
function buildWeeks(days, weekStart, max) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let cur = new Date(today.getTime() - 364 * DAY_MS);
  cur = new Date(cur.getTime() - (cur.getDay() - weekStart + 7) % 7 * DAY_MS); // back to week start

  const weeks = [];
  const months = [];
  let lastMonth = -1;
  while (cur <= today) {
    const week = [];
    let weekMonth = -1;
    for (let i = 0; i < 7; i++) {
      if (cur > today) {
        week.push(null);
      } else {
        const key = ymd(cur);
        const count = days[key] || 0;
        if (weekMonth < 0) weekMonth = cur.getMonth();
        week.push({
          date: key,
          count,
          lvl: level(count, max)
        });
      }
      cur = new Date(cur.getTime() + DAY_MS);
    }
    // Month label appears above the first week of each new month.
    if (weekMonth >= 0 && weekMonth !== lastMonth) {
      months.push({
        idx: weeks.length,
        label: new Date(2000, weekMonth, 1).toLocaleDateString(loc(), {
          month: 'short'
        })
      });
      lastMonth = weekMonth;
    }
    weeks.push(week);
  }
  return {
    weeks,
    months
  };
}
function earnedBadges(d) {
  const out = [];
  STREAK_BADGES.forEach(b => {
    if (d.streak.longest >= b.n) out.push({
      icon: b.icon,
      label: t(b.key)
    });
  });
  TOTAL_BADGES.forEach(b => {
    if (d.total >= b.n) out.push({
      icon: b.icon,
      label: t(b.key)
    });
  });
  return out;
}

/**
 * The marquee engagement widget: a year-long contribution heatmap with streaks
 * and earned badges. Self-fetches by `attrs.userId`. POJO component so it drops
 * into a profile, a modal, or anywhere. Renders nothing if the data errors, so
 * it can never break its host page.
 */
const ActivityHeatmap = {
  oninit(vnode) {
    this.data = null;
    this.error = false;
    const uid = Number(vnode.attrs.userId);
    if (uid) {
      // `false` is the stored failure, so a profile whose activity errors is
      // asked once per TTL rather than on every remount.
      const apply = d => {
        if (d) this.data = d;else this.error = true;
      };
      const fresh = peek('activity:' + uid);
      if (fresh !== undefined) apply(fresh);else load('activity:' + uid, () => userActivity(uid), false).then(d => {
        apply(d);
        m.redraw();
      });
    } else {
      this.error = true;
    }
  },
  view() {
    if (this.error) return null;
    const d = this.data;
    if (!d) {
      return m(CalWidgetSkeleton, {
        block: 'CalHeat',
        bar: 'CalHeat-bar',
        rows: 2,
        fallback: 140
      });
    }
    const weekStart = app.forum.attribute('ernestdefoe-calendar.weekStartsOn') || 0;
    const _buildWeeks = buildWeeks(d.days, weekStart, d.max),
      weeks = _buildWeeks.weeks,
      months = _buildWeeks.months;
    const badges = earnedBadges(d);
    return m('.CalHeat', measure('CalHeat'), [m('.CalHeat-head', [stat(d.total, t('heat_contributions')), stat([d.streak.current, m('span.CalHeat-fire', ' 🔥')], t('heat_current_streak')), stat(d.streak.longest, t('heat_longest_streak')), stat(d.activeDays, t('heat_active_days'))]), m('.CalHeat-scroll', m('.CalHeat-cal', [m('.CalHeat-months', months.map(mo => m('span.CalHeat-month', {
      style: {
        '--col': mo.idx
      }
    }, mo.label))), m('.CalHeat-grid', weeks.map(week => m('.CalHeat-week', week.map(cell => cell ? m('span.CalHeat-cell', {
      'data-lvl': cell.lvl,
      title: t('heat_cell', {
        count: cell.count,
        date: cell.date
      })
    }) : m('span.CalHeat-cell.is-future')))))])), m('.CalHeat-foot', [badges.length ? m('.CalHeat-badges', badges.map(b => m('span.CalHeat-badge', {
      title: b.label
    }, [b.icon, ' ', m('span.CalHeat-badgeLbl', b.label)]))) : m('span'), m('.CalHeat-legend', [m('span.CalHeat-legendLbl', t('heat_less')), [0, 1, 2, 3, 4].map(l => m('span.CalHeat-cell', {
      'data-lvl': l
    })), m('span.CalHeat-legendLbl', t('heat_more'))])])]);
  }
};
function stat(value, label) {
  return m('.CalHeat-stat', [m('span.CalHeat-statNum', value), m('span.CalHeat-statLbl', label)]);
}
export default ActivityHeatmap;
flarum.reg.add('ernestdefoe-calendar', 'forum/components/ActivityHeatmap', ActivityHeatmap);